import React, { useState, useEffect } from 'react';
import { dashboardService } from '../services/api';
import LeadDetailsModal from './LeadDetailsModal';

interface DashboardStatsData {
  total: number;
  total_whatsapp: number;
  total_compradas: number;
  status_busca_reu: Array<{
    status_busca_reu: string;
    count: number;
  }>;
  status_lead: Array<{
    status_lead: string;
    count: number;
  }>;
  top_consultores: Array<{
    consultor: string;
    count: number;
  }>;
}

const DashboardStats: React.FC = () => {
  const [stats, setStats] = useState<DashboardStatsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [modalFilter, setModalFilter] = useState<{
    type: 'comprou' | 'status' | 'user';
    value: string | boolean;
    title: string;
  } | null>(null);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoading(true);
        const data = await dashboardService.getStats();
        setStats(data);
      } catch (err) {
        setError('Erro ao carregar estatísticas');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  const handleCardClick = (type: 'comprou' | 'status' | 'user', value: string | boolean, title: string) => {
    setModalFilter({ type, value, title });
    setModalOpen(true);
  };

  const getLeadTitle = (status: string, count: number) => {
    return `Leads: ${status} (${count.toLocaleString('pt-BR')})`;
  };

  const getStatusLeadTitle = (status: string, count: number) => {
    return `Leads: ${formatStatusName(status)} (${count.toLocaleString('pt-BR')})`;
  };

  const getConsultorTitle = (consultor: string, count: number) => {
    return `Leads de ${consultor} (${count.toLocaleString('pt-BR')})`;
  };

  if (loading) {
    return (
      <div className="animate-pulse">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="bg-white rounded-lg shadow p-6">
              <div className="h-4 bg-gray-200 rounded mb-2"></div>
              <div className="h-8 bg-gray-200 rounded"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error || !stats) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-6">
        {error || 'Erro ao carregar estatísticas'}
      </div>
    );
  }

  const getStatusColor = (status: string) => {
    const colors: { [key: string]: string } = {
      'ENCONTRADO': 'bg-green-100 text-green-800 border-green-200',
      'RIC_WHATSAPP_SEND': 'bg-blue-100 text-blue-800 border-blue-200',
      'ENCONTRADO WHATSAPP': 'bg-purple-100 text-purple-800 border-purple-200'
    };
    return colors[status] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const getLeadStatusColor = (status: string) => {
    const colors: { [key: string]: string } = {
      novo: 'bg-blue-500',
      contato_inicial: 'bg-yellow-500',
      em_negociacao: 'bg-orange-500',
      proposta_enviada: 'bg-purple-500',
      fechado_ganho: 'bg-green-500',
      fechado_perdido: 'bg-red-500',
      arquivado: 'bg-gray-500'
    };
    return colors[status] || 'bg-gray-500';
  };

  const formatStatusName = (status: string) => {
    return status.replace('_', ' ').toUpperCase();
  };

  return (
    <div className="space-y-8">
      {/* Cards de Resumo */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg shadow-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-100 text-sm font-medium uppercase tracking-wide">
                Total de Leads
              </p>
              <p className="text-3xl font-bold">
                {(stats.total || 0).toLocaleString('pt-BR')}
              </p>
            </div>
            <div className="bg-blue-400 bg-opacity-50 rounded-full p-3">
              <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 20 20">
                <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg shadow-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-100 text-sm font-medium uppercase tracking-wide">
                Com WhatsApp
              </p>
              <p className="text-3xl font-bold">
                {(stats.total_whatsapp || 0).toLocaleString('pt-BR')}
              </p>
            </div>
            <div className="bg-green-400 bg-opacity-50 rounded-full p-3">
              <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10c0 3.866-3.582 7-8 7a8.841 8.841 0 01-4.083-.98L2 17l1.338-3.123C2.493 12.767 2 11.434 2 10c0-3.866 3.582-7 8-7s8 3.134 8 7zM7 9H5v2h2V9zm8 0h-2v2h2V9zM9 9h2v2H9V9z" clipRule="evenodd" />
              </svg>
            </div>
          </div>
        </div>

        <div 
          onClick={() => handleCardClick('comprou', true, `Leads que Compraram (${(stats.total_compradas || 0).toLocaleString('pt-BR')})`)}
          className="bg-gradient-to-r from-purple-500 to-purple-600 text-white rounded-lg shadow-lg p-6 cursor-pointer hover:from-purple-600 hover:to-purple-700 transition-all duration-200 hover:scale-105"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-purple-100 text-sm font-medium uppercase tracking-wide">
                Compradas
              </p>
              <p className="text-3xl font-bold">
                {(stats.total_compradas || 0).toLocaleString('pt-BR')}
              </p>
            </div>
            <div className="bg-purple-400 bg-opacity-50 rounded-full p-3">
              <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 20 20">
                <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z" />
              </svg>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-r from-yellow-500 to-yellow-600 text-white rounded-lg shadow-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-yellow-100 text-sm font-medium uppercase tracking-wide">
                Taxa WhatsApp
              </p>
              <p className="text-3xl font-bold">
                {stats.total > 0 ? (((stats.total_whatsapp || 0) / stats.total) * 100).toFixed(1) : '0.0'}%
              </p>
            </div>
            <div className="bg-yellow-400 bg-opacity-50 rounded-full p-3">
              <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M3 3a1 1 0 000 2v8a2 2 0 002 2h2.586l-1.293 1.293a1 1 0 101.414 1.414L10 15.414l2.293 2.293a1 1 0 001.414-1.414L12.414 15H15a2 2 0 002-2V5a1 1 0 100-2H3zm11.707 4.707a1 1 0 00-1.414-1.414L10 9.586 8.707 8.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            </div>
          </div>
        </div>
      </div>

      {/* Status das Buscas */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="bg-white rounded-lg shadow-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-6">Status da Busca de Dados</h3>
          <div className="space-y-4">
            {stats.status_busca_reu && stats.status_busca_reu.map((item) => (
              <div 
                key={item.status_busca_reu} 
                onClick={() => handleCardClick('status', item.status_busca_reu, getLeadTitle(item.status_busca_reu, item.count))}
                className={`border rounded-lg p-4 cursor-pointer hover:shadow-md transition-all duration-200 hover:scale-[1.02] ${getStatusColor(item.status_busca_reu)}`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-semibold">{item.status_busca_reu}</p>
                    <p className="text-sm opacity-75">
                      {((item.count / stats.total) * 100).toFixed(1)}% do total
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold">{item.count.toLocaleString('pt-BR')}</p>
                    <p className="text-sm opacity-75">leads</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Status dos Leads */}
        <div className="bg-white rounded-lg shadow-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-6">Status dos Leads</h3>
          <div className="space-y-3">
            {stats.status_lead && stats.status_lead.slice(0, 8).map((item) => (
              <div 
                key={item.status_lead} 
                onClick={() => handleCardClick('status', item.status_lead, getStatusLeadTitle(item.status_lead, item.count))}
                className="flex items-center justify-between p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 hover:shadow-md transition-all duration-200 hover:scale-[1.02]"
              >
                <div className="flex items-center">
                  <div className={`w-4 h-4 rounded-full ${getLeadStatusColor(item.status_lead)} mr-3`}></div>
                  <span className="font-medium text-gray-900">
                    {formatStatusName(item.status_lead)}
                  </span>
                </div>
                <div className="text-right">
                  <span className="text-lg font-semibold text-gray-900">
                    {item.count.toLocaleString('pt-BR')}
                  </span>
                  <p className="text-xs text-gray-500">
                    {((item.count / stats.total) * 100).toFixed(1)}%
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Top Consultores */}
      <div className="bg-white rounded-lg shadow-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-6">Top 10 Consultores</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          {stats.top_consultores && stats.top_consultores.length > 0 ? (
            stats.top_consultores.map((item, index) => (
              <div
                key={item.consultor}
                onClick={() => handleCardClick('user', item.consultor, getConsultorTitle(item.consultor, item.count))}
                className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg p-4 border cursor-pointer hover:from-gray-100 hover:to-gray-200 hover:shadow-md transition-all duration-200 hover:scale-105"
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-600">#{index + 1}</span>
                  <span className="text-xs text-gray-500">
                    {((item.count / stats.total) * 100).toFixed(1)}%
                  </span>
                </div>
                <p className="font-semibold text-gray-900 text-sm mb-1">{item.consultor}</p>
                <p className="text-lg font-bold text-blue-600">{item.count.toLocaleString('pt-BR')}</p>
              </div>
            ))
          ) : (
            <div className="col-span-full text-center text-gray-500 py-8">
              Nenhum consultor com leads atribuídas
            </div>
          )}
        </div>
      </div>

      {/* Modal para detalhes dos leads */}
      {modalOpen && modalFilter && (
        <LeadDetailsModal
          isOpen={modalOpen}
          onClose={() => {
            setModalOpen(false);
            setModalFilter(null);
          }}
          filter={modalFilter}
        />
      )}
    </div>
  );
};

export default DashboardStats;